import { mapState } from 'vuex'

import context from './context'
import DateRangeCalculator from '@/classes/helpers/DateRangeCalculator'

const defaultCurrency = 'GBP'

const datePlaceholder = { startDate: null, endDate: null }

const calculateRange = (interval, date, dateRangeCalculator) => {
  const ranges = {
    'day': date => dateRangeCalculator.getHourRange(date),
    'week': date => dateRangeCalculator.getWeek(date),
    'month': date => dateRangeCalculator.getMonth(date),
    'year': date => dateRangeCalculator.getYear(date)
  }

  return ranges[interval](date)
}

const calculatePreviousDateRange = (interval, { startDate, endDate }, dateRangeCalculator) => {
  const previousDate = {
    'day': date => dateRangeCalculator.moment(date).subtract(1, 'year'),
    'week': date => dateRangeCalculator.moment(date).subtract(1, 'year').week(date.week()),
    'month': date => dateRangeCalculator.moment(date).subtract(1, 'year').month(date.month()),
    'year': date => dateRangeCalculator.moment(date).subtract(1, 'year'),
    'custom': date => dateRangeCalculator.moment(date).subtract(1, 'year')
  }

  return interval === 'custom' ? { startDate: previousDate[interval](startDate), endDate: previousDate[interval](endDate) } : calculateRange(interval, previousDate[interval](startDate), dateRangeCalculator)
}

export default {
  data () {
    return {
      selectedCurrency: null,

      isLoading: true,
      isLoadingInterval: false,

      isCompareActive: false,

      chartType: 'bar',
      dataType: 'volume',
      interval: 'day',
      displayInterval: 'day',
      intervalSlideIndex: 0,

      customDate: datePlaceholder,

      dataTypes: [
        { value: 'volume', label: this.$i18n.t('label.transactionCount') },
        { value: 'revenue', label: this.$i18n.t('label.salesRevenue'), isCurrencyRequired: true },
        { value: 'unsuccessfulVolume', label: this.$i18n.t('label.declineCount') },
        { value: 'unsuccessfulRevenue', label: this.$i18n.t('label.declineAmount'), isCurrencyRequired: true },
        { value: 'salesCount', label: this.$i18n.t('label.totalSalesCount') }
      ]
    }
  },

  mixins: [ context ],

  created () {
    this.dateRangeCalculator = new DateRangeCalculator(this.$moment)

    if (this.$store.getters.merchantCurrencies(this.context.merchant.id).some(currency => currency === defaultCurrency)) {
      this.selectedCurrency = defaultCurrency
    }
  },

  mounted () {
    this.initialize().then(() => {
      this.$watch(() => this.interval, interval => {
        if (interval !== 'custom') {
          this.isLoading = true
          this.isCompareActive = false

          this.intervalSlideIndex = 0
          this.chartType = 'bar'
          this.customDate = datePlaceholder

          Promise.all([this.getIntervalOptions(interval, this.$moment()), this.getStatistics(interval, { startDate: this.$moment() })]).finally(() => {
            this.displayInterval = interval

            this.isLoading = false
          })
        }
      })

      this.$watch(() => this.selectedCurrency, () => this.reload(), { immediate: true })

      this.$watch(() => this.filters.map(filter => filter.value), () => this.reload())

      this.$watch(() => this.isCompareActive, isCompareActive => {
        if (isCompareActive) {
          this.isLoading = true

          const { startDate, endDate } = this.currentStatistics
          const range = calculatePreviousDateRange(this.interval, { startDate, endDate }, this.dateRangeCalculator)

          if (this.previousStatistics === null) {
            this.getPreviousStatistics(this.interval, range, true)
          }

          this.getPreviousStatistics(this.interval, range, false).finally(() => {
            this.isLoading = false
          })
        } else {
          this.$store.dispatch('clearPreviousStatistics')
        }
      })
    })
  },

  activated () {
    if (this.isLoading === false && (this.statistics.intervalOptions === null || this.statistics.intervalStatistics.current === null)) {
      this.initialize(false)
    }
  },

  computed: {
    ...mapState({
      statistics: state => state.statistics,
      currentStatistics: state => state.statistics.intervalStatistics.current,
      previousStatistics: state => state.statistics.intervalStatistics.previous
    }),

    /**
     * Default empty filter array
     */
    filters: {
      get () {
        return []
      },
      set () { }
    },

    intervalSlides () {
      const intervalFormat = {
        'day': 'DD MMM YYYY',
        'week': '[WK ]W',
        'month': 'MMM YYYY',
        'year': 'YYYY'
      }

      if (this.statistics.intervalOptions) {
        const { datapoints = [] } = this.statistics.intervalOptions

        const maxValue = Math.max(...datapoints.map(interval => parseFloat(interval[this.dataType] || 0)))

        return datapoints.map(datapoint => {
          return {
            title: datapoint.startDate.format(intervalFormat[this.interval]),
            subtitle: this.interval === 'week' ? this.getSubtitle(datapoint) : null,
            fillHeight: ((100 * parseFloat(datapoint[this.dataType] || 0)) / maxValue) || 0,
            data: datapoint }
        }).reverse()
      }

      return []
    },

    categories () {
      const categoryIntervalFormat = {
        'day': 'HH:mm',
        'week': 'ddd',
        'month': 'DD',
        'year': 'MMM',
        'custom': 'DD MMM'
      }

      if (this.currentStatistics !== null && this.currentStatistics.datapoints.length > 0) {
        if (this.isCompareActive === false || this.previousStatistics === null) {
          return this.currentStatistics.datapoints.map(datapoint => datapoint.startDate.format(categoryIntervalFormat[this.displayInterval]))
        } else {
        return this.currentStatistics.datapoints.length >= this.previousStatistics.datapoints.length
          ? this.currentStatistics.datapoints.map(datapoint => datapoint.startDate.format(categoryIntervalFormat[this.displayInterval]))
          : this.previousStatistics.datapoints.map(datapoint => datapoint.startDate.format(categoryIntervalFormat[this.displayInterval]))
        }
      }

      return []
    },

    series () {
      const nameFormats = {
        'day': (statistics) => statistics.startDate.format('DD MMM YYYY'),
        'week': (statistics) => `${statistics.startDate.format('[WK ]W YYYY')} (${this.getSubtitle(statistics, false)})`,
        'month': (statistics) => statistics.startDate.format('MMM YYYY'),
        'year': (statistics) => statistics.startDate.format('YYYY'),
        'custom': (statistics) => this.getSubtitle(statistics)
      }

      const labelFormats = {
        'day': (datapoint) => datapoint.startDate.format('HH:mm DD MMM YYYY'),
        'week': (datapoint) => datapoint.startDate.format('ddd DD MMM YYYY'),
        'month': (datapoint) => datapoint.startDate.format('DD MMM YYYY'),
        'year': (datapoint) => datapoint.startDate.format('MMM YYYY'),
        'custom': (datapoint) => this.getSubtitle(datapoint)
      }

      if (this.currentStatistics !== null && this.currentStatistics.datapoints.length > 0) {
        const series = [{
          name: nameFormats[this.displayInterval](this.currentStatistics),
          data: this.currentStatistics.datapoints.map(datapoint => datapoint[this.dataType] || 0),
          labels: this.currentStatistics.datapoints.map(datapoint => labelFormats[this.displayInterval](datapoint))
        }]

        if (this.isCompareActive === true && this.previousStatistics !== null && this.previousStatistics.datapoints.length > 0) {
          series.unshift({
            name: nameFormats[this.displayInterval](this.previousStatistics),
            data: this.previousStatistics.datapoints.map(datapoint => datapoint[this.dataType] || 0),
            labels: this.previousStatistics.datapoints.map(datapoint => labelFormats[this.displayInterval](datapoint))
          })
        }

        return series
      }

      return []
    }
  },

  methods: {
    initialize (isPlaceholder = true) {
      this.isLoading = true

      return Promise.all([this.getIntervalOptions(this.interval, this.$moment(), isPlaceholder), this.getStatistics(this.interval, { startDate: this.$moment() }, isPlaceholder)]).finally(() => {
        this.isLoading = false
      })
    },

    reload () {
      this.isLoading = true

      Promise.all([this.getIntervalOptions(this.interval, this.statistics.intervalOptions.endDate), this.getStatistics(this.interval, this.currentStatistics)]).finally(() => {
        this.isLoading = false
      })
    },

    getSubtitle ({ startDate, endDate = null }, includeYear = true) {
      if (endDate === null) {
        endDate = this.$moment(startDate).endOf(this.interval)
      }

      return startDate.isSame(endDate, 'day')
        ? startDate.format(`DD MMM${ includeYear ? ' YYYY' : ''}`)
        : startDate.isSame(endDate, 'month')
          ? `${startDate.format('DD')} - ${endDate.format(`DD MMM${ includeYear ? ' YYYY' : ''}`)}`
          : startDate.isSame(endDate, 'year')
            ? `${startDate.format('DD MMM')} - ${endDate.format(`DD MMM${ includeYear ? ' YYYY' : ''}`)}`
            : `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`
    },

    onIntervalEnd ({ startDate }) {
      this.isLoadingInterval = true

      this.getIntervalOptions(this.interval, this.$moment(startDate).subtract(1, this.interval), false, true).finally(() => {
        this.isLoadingInterval = false
      })
    },

    onIntervalClick (range) {
      this.isLoading = true

      this.getCurrentStatistics(this.interval, range).finally(() => {
        this.isLoading = false
      })
    },

    onApplyClick ({ startDate, endDate }) {
      this.isLoading = true

      if (this.interval !== 'custom') {
        this.interval = 'custom'
        this.isCompareActive = false
      }

      this.getCurrentStatistics(this.interval, { startDate: this.$moment(startDate), endDate: this.$moment(endDate) }).finally(() => {
        this.chartType = 'line'
        this.displayInterval = this.interval

        this.isLoading = false
      })
    },

    onCompareApplyCLick (range) {
      this.isLoading = true

      this.getPreviousStatistics(this.interval, range).finally(() => {
        this.isLoading = false
      })
    },

    getIntervalOptions (interval, date, isPlaceholder = false, isPrepend = false) {
      if (interval !== 'custom') {
        const amount = 15

        const startDate = this.$moment(date).startOf(interval).subtract(amount, `${interval}s`)

        const intervalRanges = {
          'day': (startDate, amount) => this.dateRangeCalculator.getDays(startDate, amount),
          'week': (startDate, amount) => this.dateRangeCalculator.getWeeks(startDate, amount),
          'month': (startDate, amount) => this.dateRangeCalculator.getMonths(startDate, amount),
          'year': (startDate, amount) => this.dateRangeCalculator.getYears(startDate, amount)
        }

        const { endDate } = intervalRanges[interval](startDate, amount)

        if (!isPrepend) {
          // Reset interval values
          this.$store.dispatch('getIntervalOptions', { intervalRange: { interval, startDate, endDate }, isPlaceholder: true })
        }

        const parameters = this.filters.filter(filter => filter.hasValue()).reduce((parameters, filter) => {
          return { ...parameters, [filter.property.toLowerCase()]: filter.toString().toLowerCase() }
        }, {})

        return this.$store.dispatch('getIntervalOptions', { context: this.context, intervalRange: { interval: interval, startDate, endDate }, parameters: { currency: this.selectedCurrency, ...parameters }, isPlaceholder, isPrepend })
      }
    },

    getStatistics (interval, { startDate, endDate }, isPlaceholder) {
      const range = interval === 'custom' ? { startDate, endDate } : calculateRange(interval, startDate, this.dateRangeCalculator)

      const previousRange = this.previousStatistics === null ? calculatePreviousDateRange(interval, range, this.dateRangeCalculator) : { startDate: this.previousStatistics.startDate, endDate: this.previousStatistics.endDate }
      const previousStatistics = new Promise(resolve => this.isCompareActive ? this.getPreviousStatistics(interval, previousRange, isPlaceholder).then(resolve) : resolve())

      return Promise.all([this.getCurrentStatistics(interval, range, isPlaceholder), previousStatistics])
    },

    getCurrentStatistics (interval, range, isPlaceholder) {
      return new Promise(resolve => {
        this.getStatisticsByAction('getCurrentStatistics', interval, range, isPlaceholder).then(statistic => {
          if (statistic.datapoints.length === 0) {
            this.$store.dispatch('showMessage', { title: 'No graph data available.', autoHideDelay: 5000, options: { variant: 'danger' } })
          }

          resolve(statistic)
        })
      })
    },

    getPreviousStatistics (interval, range, isPlaceholder) {
      return new Promise(resolve => {
        this.getStatisticsByAction('getPreviousStatistics', interval, range, isPlaceholder).then(statistic => {
          if (statistic.datapoints.length === 0) {
            this.$store.dispatch('showMessage', { title: 'No graph data available.', autoHideDelay: 5000, options: { variant: 'danger' } })
          }

          resolve(statistic)
        })
      })
    },

    getStatisticsByAction (action, interval, { startDate, endDate }, isPlaceholder = false) {
      const timespans = {
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'year': 'month',
        'custom': 'day'
      }

      const parameters = this.filters.filter(filter => filter.hasValue()).reduce((parameters, filter) => {
        return { ...parameters, [filter.property.toLowerCase()]: filter.toString().toLowerCase() }
      }, {})

      return this.$store.dispatch(action, { context: this.context, intervalRange: { interval: timespans[interval], startDate, endDate }, parameters: { currency: this.selectedCurrency, ...parameters }, isPlaceholder })
    }
  }
}
